import { FormEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { HttpError } from '@rossum/api-client/errors';
import { Label } from '@rossum/api-client/labels';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import {
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import ActionDialog from '../../../components/UI/ActionDialog';
import ActionDialogActions from '../../../components/UI/ActionDialog/ActionDialogActions';
import { boldText } from '../../../lib/formaterValues';
import { useCreateLabel } from '../hooks/useCreateLabel';
import { useDeleteLabel } from '../hooks/useDeleteLabel';
import { useEditLabel } from '../hooks/useEditLabel';
import { ColorPicker } from './ColorPicker';

type LabelState = {
  name: string;
  color?: string | null;
};

type LabelDialogProps = {
  id: number;
  onClose: () => void;
} & LabelState;

const DIALOG_WIDTH = 448;

const handleDialogSubmit = (onSubmit: () => void) => (e: FormEvent) => {
  e.preventDefault();
  onSubmit();
};

const DialogContent = ({
  onNameChange,
  onColorChange,
  color,
  name,
  id,
  errorMessages,
}: {
  onNameChange: (name: string) => void;
  onColorChange: (color: string) => void;
  color?: string | null;
  name: string;
  id?: string;
  errorMessages?: Partial<{
    name: string;
    color: string;
  }>;
}) => {
  const intl = useIntl();

  const nameErrorMessage = errorMessages?.name;
  const colorErrorMessage = errorMessages?.color;

  return (
    <Stack spacing={2} minWidth={DIALOG_WIDTH} py={1}>
      {id ? (
        <TextField
          value={id}
          disabled
          slotProps={{
            input: {
              endAdornment: (
                <CopyToClipboardButton content={id} iconColor="secondary" />
              ),
            },
          }}
          size="small"
          label={intl.formatMessage({
            id: 'containers.settings.labels.labelId',
          })}
        />
      ) : null}

      <TextField
        label={intl.formatMessage({
          id: 'containers.settings.labels.dialogs.fields.labelName',
        })}
        value={name}
        onChange={e => onNameChange(e.target.value)}
        autoFocus
        size="small"
        helperText={
          <Typography variant="caption" color="error">
            {nameErrorMessage}
          </Typography>
        }
        slotProps={{
          htmlInput: {
            'data-cy': 'label-name',
          },
        }}
      />
      <ColorPicker onColorChange={onColorChange} color={color} />
      {colorErrorMessage ? (
        <Typography variant="caption" color="error" maxWidth={350}>
          {colorErrorMessage}
        </Typography>
      ) : null}
    </Stack>
  );
};

const labelErrorSchema = z
  .object({
    name: z.array(z.string()),
    color: z.array(z.string()),
  })
  .partial();

const getLabelErrors = (error: unknown) => {
  const errorData = error instanceof HttpError ? error.data : undefined;
  const parsedErrors = labelErrorSchema.safeParse(errorData);

  return parsedErrors.success
    ? {
        color: parsedErrors.data.color?.join('\n'),
        name: parsedErrors.data.name?.join('\n'),
      }
    : undefined;
};

export const EditLabelDialog = ({
  name,
  id,
  color,
  onClose,
}: LabelDialogProps) => {
  const [labelState, setLabelState] = useState<LabelState>({ name, color });

  const intl = useIntl();
  const { mutate: editLabel, isLoading, error } = useEditLabel();

  const errorMessages = getLabelErrors(error);

  return (
    <form
      onSubmit={handleDialogSubmit(() => {
        return editLabel(
          {
            labelId: id,
            name: labelState.name,
            color: labelState.color ? labelState.color : null,
          },
          { onSuccess: onClose }
        );
      })}
    >
      <ActionDialog
        dialogTitle={intl.formatMessage({
          id: 'containers.settings.labels.dialogs.editTitle',
        })}
        dialogColor="primary"
        onCancel={onClose}
        dialogActions={
          <ActionDialogActions
            confirmButtonProps={{
              type: 'submit',
              startIcon: isLoading ? <CircularProgress size={14} /> : null,
              disabled:
                isLoading || !labelState || labelState.name.length === 0,
            }}
            confirmButtonText={intl.formatMessage({
              id: 'containers.settings.labels.dialogs.editButton',
            })}
            cancelButtonText={intl.formatMessage({
              id: 'containers.settings.labels.dialogs.cancelButton',
            })}
            dataCyConfirm="edit-label-dialog-confirm-button"
            dataCyCancel="edit-label-dialog-cancel-button"
          />
        }
        dataCy="edit-label-dialog"
      >
        <DialogContent
          id={id.toString()}
          name={labelState.name}
          color={labelState.color}
          onNameChange={value =>
            setLabelState(prevState => ({ ...prevState, name: value }))
          }
          onColorChange={value =>
            setLabelState(prevState => ({ ...prevState, color: value }))
          }
          errorMessages={errorMessages}
        />
      </ActionDialog>
    </form>
  );
};

export const CreateLabelDialog = ({
  onClose,
  onSuccess,
}: Pick<LabelDialogProps, 'onClose'> & {
  onSuccess?: (label: Label) => void;
}) => {
  const [labelState, setLabelState] = useState<LabelState>({ name: '' });

  const intl = useIntl();

  const { mutate: createLabel, isLoading, error } = useCreateLabel();

  const errorMessages = getLabelErrors(error);

  return (
    <form
      onSubmit={handleDialogSubmit(() =>
        createLabel(
          {
            name: labelState.name,
            color: labelState.color ? labelState.color : undefined,
          },
          {
            onSuccess: data => {
              onSuccess?.(data);
              onClose();
            },
          }
        )
      )}
    >
      <ActionDialog
        dialogTitle={intl.formatMessage({
          id: 'containers.settings.labels.dialogs.createTitle',
        })}
        dialogColor="primary"
        onCancel={onClose}
        dialogActions={
          <ActionDialogActions
            confirmButtonProps={{
              type: 'submit',
              startIcon: isLoading ? <CircularProgress size={14} /> : null,
              disabled:
                isLoading || !labelState || labelState.name.length === 0,
            }}
            confirmButtonText={intl.formatMessage({
              id: 'containers.settings.labels.dialogs.createButton',
            })}
            cancelButtonText={intl.formatMessage({
              id: 'containers.settings.labels.dialogs.cancelButton',
            })}
            dataCyConfirm="create-label-dialog-confirm-button"
            dataCyCancel="create-label-dialog-cancel-button"
          />
        }
        dataCy="create-label-dialog"
      >
        <DialogContent
          name={labelState.name}
          color={labelState.color}
          onNameChange={value =>
            setLabelState(prevState => ({ ...prevState, name: value }))
          }
          onColorChange={value =>
            setLabelState(prevState => ({ ...prevState, color: value }))
          }
          errorMessages={errorMessages}
        />
      </ActionDialog>
    </form>
  );
};

export const DeleteLabelDialog = ({ name, onClose, id }: LabelDialogProps) => {
  const { mutate: deleteLabel, isLoading } = useDeleteLabel();
  const intl = useIntl();

  return (
    <form
      onSubmit={handleDialogSubmit(() =>
        deleteLabel(id, { onSuccess: onClose })
      )}
    >
      <ActionDialog
        dialogTitle={intl.formatMessage(
          {
            id: 'containers.settings.labels.dialogs.deleteTitle',
          },
          { labelName: name }
        )}
        dialogColor="error"
        onCancel={onClose}
        dialogActions={
          <ActionDialogActions
            confirmButtonProps={{
              type: 'submit',
              startIcon: isLoading ? <CircularProgress size={14} /> : null,
              disabled: isLoading,
              autoFocus: true,
            }}
            confirmButtonText={intl.formatMessage({
              id: 'containers.settings.labels.dialogs.deleteButton',
            })}
            cancelButtonText={intl.formatMessage({
              id: 'containers.settings.labels.dialogs.cancelButton',
            })}
            dataCyConfirm="delete-label-dialog-confirm-button"
            dataCyCancel="delete-label-dialog-cancel-button"
          />
        }
        dataCy="delete-label-dialog"
      >
        <Stack minWidth={DIALOG_WIDTH}>
          <Typography>
            {intl.formatMessage(
              {
                id: 'containers.settings.labels.dialogs.deleteDescription',
              },
              {
                labelName: name,
                boldText,
              }
            )}
          </Typography>
        </Stack>
      </ActionDialog>
    </form>
  );
};
